import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { StateService } from '@uirouter/core';
import { MenuService } from '../../../../layout/services/menu.service';
import { MonitoringAlarmSection } from '../../../project/models/project-configuration';
import { MenuBehaviorSubjectService } from '../../../../shared/services/menu-behavior-subject.service';
import { Subscription, Observable, BehaviorSubject, interval, of, merge } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { TableStateService } from '../../../../shared/services/table-state.service';
import { FaultMonitoringDataService } from "../../../../features/monitoring/services/fault-monitoring-data.service";
import { MonitoringAlarm } from '../../models/monitoring-alarm';
import { MessageService } from 'primeng/api';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-monitoring-alarms',
  template: require('./monitoring-alarms.component.html'),
  styles: [require('./monitoring-alarms.component.sass')]
})
export class MonitoringAlarmsComponent implements OnInit, OnDestroy {

  monitoringAlarmSection: MonitoringAlarmSection | undefined;
  menuChangeSubscription: Subscription;
  languageChangeSubscription: Subscription;
  pollingMonitoringAlarmsSubscription: Subscription;

  alarms$: Observable<MonitoringAlarm[]>;
  selectedAlarmList: MonitoringAlarm[] = [];
  polling$: Observable<number | undefined>;

  alarmDateFormat = environment.alarms.dateFormat;
  columns: any[];
  url: string;
  pageTitle: string | undefined;
  projectId: number;

  tableConfiguration: any;

  private readonly refreshAlarms$ = new BehaviorSubject(undefined);

  constructor(
    public tableState: TableStateService,
    private $state: StateService,
    private menuService: MenuService,
    private translate: TranslateService,
    private menuBehaviorSubjectService: MenuBehaviorSubjectService,
    private cdref: ChangeDetectorRef,
    private faultMonitoringDataService: FaultMonitoringDataService,
    private messageService: MessageService
  ) {
    this.url = this.$state.params.alarmType;
    this.projectId = this.$state.params && this.$state.params.projectId;

    this.polling$ = this.refreshAlarms$.pipe(
        switchMap(() => {
          return interval(environment.config.monitoring.refreshPeriod * 1000);
        })
    )

    this.pollingMonitoringAlarmsSubscription = merge(
        this.refreshAlarms$,
        this.polling$
        ).pipe(
            switchMap(() => this.faultMonitoringDataService.getMonitoringAlarms(this.projectId, this.url)))
        .subscribe((alarms) => {
            this.alarms$ = of(alarms);
            this.cdref.detectChanges();
            this.clearSelectedAlarms(alarms);
        }
    )

    this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.refreshTable();
    })
  }

  ngOnInit(): void {
    this.tableConfiguration = {
      paginate: true,
      sortableMode: 'multiple',
      defaultPagination: 250,
      itemsPerPageOptions: [100, 250, 500],
      exportFilename: 'monitoring-alarms-' + this.url
    }
    this.refreshTable();
    this.refreshAlarms$.next(undefined);
  }

  ngAfterViewInit() {
    this.menuChangeSubscription = this.menuBehaviorSubjectService.changeOccured.subscribe(data => {
      if (data?.action === "MENU_CREATED") {
        this.monitoringAlarmSection = this.menuService.getMonitoringAlarmSectionByUrl(this.$state.params.alarmType);
        if (!this.monitoringAlarmSection || String(this.monitoringAlarmSection.projectId) !== String(this.projectId)) {
          this.$state.go("home", {
            projectId: this.projectId
          });
        } else {
          this.pageTitle = this.monitoringAlarmSection?.name.find(section => section.language === this.translate.currentLang)?.text;
          this.cdref.detectChanges();
        }
      }
    })
  }

  refreshTable = () => {
    this.columns = [{
      header: this.translate.instant('I18N_MONITORING.HOST'),
      field: 'hostname',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_MONITORING.SEVERITY'),
      field: 'severity',
      type: 'text',
      disableSearch: true
    },{
      header: this.translate.instant('I18N_MONITORING.DESCRIPTION'),
      field: 'description',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_MONITORING.DATE'),
      field: 'lastChange',
      sortOrder: '-1'
    }, {
      header: this.translate.instant('I18N_MONITORING.ACKNOWLEDGED'),
      field: 'acknowledged'
    }
    ];
  }

  acknowledgeAlarm = async () => {
    const selectedAlarms = [...this.selectedAlarmList];
    if (selectedAlarms && selectedAlarms.length > 0) {
      const projectId = selectedAlarms[0].projectId;
      const eventIds = selectedAlarms.map(alarm => alarm.eventId);

      try {
        await this.faultMonitoringDataService.acknowledgeMonitoringAlarms(projectId, this.url, eventIds);
        this.selectedAlarmList = selectedAlarms.map(alarm => {
          return {
            ...alarm,
            acknowledged: true
          }
        })
      } catch (error) {

      }
    }
    this.refreshAlarms$.next(undefined);
  }

  investigateAlarm = () => {
    const alarms = [...this.selectedAlarmList];
    if (alarms && alarms.length > 0) {
      const zabbixURL = alarms[0].zabbixURL;
      if (zabbixURL) {
        const matchedResult = zabbixURL.match(/^https?:\/\/[^#?\/]+(:?\/zabbix)*/);
        if (matchedResult) {
          const zabbixBaseURL = matchedResult[0];
          for (let i = 0; i < alarms.length; i++) {
            window.open(zabbixBaseURL + '/tr_events.php?triggerid=' + alarms[i].triggerId + '&eventid=' + alarms[i].eventId);
          }
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('I18N_MONITORING.INVESTIGATE_IN_ZABBIX'),
            detail: this.translate.instant('I18N_MONITORING.NO_ZABBIX_CONFIG_URL')
          });
        }

      } else {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('I18N_MONITORING.INVESTIGATE_IN_ZABBIX'),
          detail: this.translate.instant('I18N_MONITORING.NO_ZABBIX_CONFIG_URL_SELECTED_TRAIN')
        });
      }
    }
  }

  clearSelectedAlarms = (alarms: MonitoringAlarm[]) => {
    this.selectedAlarmList = alarms && alarms.length > 0 ? alarms.filter(alarm => this.selectedAlarmList.map(alarm => alarm.eventId).includes(alarm.eventId)) : [];
  }

  ngOnDestroy() {
    this.menuChangeSubscription.unsubscribe();
    this.languageChangeSubscription.unsubscribe();
    this.pollingMonitoringAlarmsSubscription.unsubscribe();
  }

}
